import React from 'react';
import { Layout, Menu, Button, Space, Dropdown, Avatar } from 'antd';
import {
  HomeOutlined, AppstoreOutlined, UserOutlined,
  LogoutOutlined, LoginOutlined,
  PlusCircleOutlined, ShoppingOutlined,
  ReadOutlined, NotificationOutlined, FundProjectionScreenOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { SUBMIT_PAYMENT_ENABLED, contactEmail, GLOBAL_WHATSAPP_DISPLAY, GLOBAL_WHATSAPP_URL } from '../utils/site';
import RobotLogo from './RobotLogo';
import BrandText from './BrandText';
import AIChatWidget from './AIChatWidget';


const { Header, Content, Footer } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
      onClick: () => navigate('/profile'),
    },
    ...(SUBMIT_PAYMENT_ENABLED
      ? [{
          key: 'my-orders',
          icon: <ShoppingOutlined />,
          label: t('nav.myOrders'),
          onClick: () => navigate('/my-orders'),
        }]
      : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  const navItems = [
    { key: '/', icon: <HomeOutlined />, label: <Link to="/">{t('nav.home')}</Link> },
    { key: '/tools', icon: <AppstoreOutlined />, label: <Link to="/tools">{t('nav.tools')}</Link> },
    {
      key: '/rankings',
      icon: <TrophyOutlined />,
      label: <Link to="/rankings/popular">{language === 'zh-CN' ? '榜单' : 'Rankings'}</Link>,
    },
    { key: '/insights', icon: <ReadOutlined />, label: <Link to="/insights">{language === 'zh-CN' ? '洞察' : 'Insights'}</Link> },
    { key: '/news', icon: <NotificationOutlined />, label: <Link to="/news">{language === 'zh-CN' ? '选型短文' : 'Buying notes'}</Link> },
    { key: '/funding', icon: <FundProjectionScreenOutlined />, label: <Link to="/funding">{language === 'zh-CN' ? '融资' : 'Funding'}</Link> },
    { key: '/submit', icon: <PlusCircleOutlined />, label: <Link to="/submit">{t('nav.submit')}</Link> },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
          borderBottom: '1px solid rgba(0,0,0,0.04)',
          padding: '0 24px',
          transition: 'background 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <RobotLogo size={38} />
            <BrandText language={language} variant="header" size={19} />
          </Link>
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={navItems}
            style={{ border: 'none', marginLeft: 24 }}
          />
        </div>

        <Space>
          {isAuthenticated ? (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1677ff' }}
                />
                <span style={{ color: '#1a1a1a' }}>{user?.username}</span>
              </Space>
            </Dropdown>
          ) : (
            <Space>
              <Button type="primary" icon={<LoginOutlined />} onClick={() => navigate('/login')}>
                {t('nav.login')}
              </Button>
            </Space>
          )}
        </Space>
      </Header>

      <Content style={{ flex: 1 }}>
        {children}
      </Content>

      <Footer className="app-footer">
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <RobotLogo size={34} />
            <BrandText language={language} variant="footer" size={18} />
          </div>
          <p style={{ marginBottom: 16 }}>
            {t('footer.desc')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 24, flexWrap: 'wrap' }}>
            <Link to="/tools">{t('nav.tools')}</Link>
            <Link to="/disclaimer">{t('footer.disclaimer')}</Link>
            <Link to="/privacy">{t('footer.privacy')}</Link>
            <Link to="/submit">{t('footer.submit')}</Link>
            <Link to="/feedback">{t('footer.feedback')}</Link>
            <Link to="/contact">{t('footer.contact')}</Link>
          </div>
          <p style={{ fontSize: 12, opacity: 0.5 }}>
            © 2024-2026 {language === 'zh-CN' ? 'AI速览局' : 'GlobalAI Tools'}. All rights reserved. | {t('footer.legalNotice')}
          </p>
          <p style={{ fontSize: 12, opacity: 0.4, marginTop: 4 }}>
            Qixin Net (Beijing) Technology Co., Ltd. |{' '}
            <a href={`mailto:${contactEmail()}`} style={{ color: 'inherit' }}>{contactEmail()}</a>
            {' · '}
            <a href={GLOBAL_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
              WhatsApp {GLOBAL_WHATSAPP_DISPLAY}
            </a>
          </p>
        </div>
      </Footer>
      <AIChatWidget />
    </Layout>
  );
};

export default AppLayout;
